import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import {
  listFineConfigs, createFineConfig, updateFineConfig, deleteFineConfig,
  getPaymentConfig, createPaymentConfig, updatePaymentConfig,
  getSucursal, updateSucursalPautaConfig,
} from "../services/config";

const TABS = [
  { key: "multas", labelKey: "config.tab.multas", icon: "mdi:cash-remove" },
  { key: "pagamentos", labelKey: "config.tab.pagamentos", icon: "mdi:calendar-cash" },
  { key: "pautas", labelKey: "config.tab.pautas", icon: "mdi:file-table-outline" },
];

// Uma flag é considerada "ligada" a não ser que esteja explicitamente a 0 —
// sucursais sem configuração ainda mantêm o comportamento anterior à
// existência desta página (ver routes/api/whatsapp.js#handlePedagogicalPerformance).
const isFlagOn = (value) => !(value === 0 || value === "0");

const ConfiguracoesLayer = ({ initialTab = "multas" }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab);

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <ul className='nav nav-pills mb-24 gap-2' role='tablist'>
              {TABS.map((tab) => (
                <li className='nav-item' role='presentation' key={tab.key}>
                  <button
                    type='button'
                    className={`nav-link radius-8 text-sm d-flex align-items-center gap-1 ${activeTab === tab.key ? "active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <Icon icon={tab.icon} />
                    {t(tab.labelKey)}
                  </button>
                </li>
              ))}
            </ul>

            {activeTab === "multas" && <MultasTab t={t} />}
            {activeTab === "pagamentos" && <PagamentosTab t={t} />}
            {activeTab === "pautas" && <PautasTab t={t} />}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Multas — faixas de dias de atraso -> percentagem de multa (fine_config)
// ---------------------------------------------------------------------------
const emptyFine = { startDate: "", endDate: "", percentage: "" };

const MultasTab = ({ t }) => {
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyFine);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchFines = () => {
    setLoading(true);
    listFineConfigs()
      .then((resp) => setFines((resp.data || []).slice().sort((a, b) => Number(a.startDate) - Number(b.startDate))))
      .catch(() => toast.error(t("config.multa.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(fetchFines, [t]);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const startEdit = (fine) => {
    setEditingId(fine.id);
    setForm({ startDate: fine.startDate, endDate: fine.endDate, percentage: fine.percentage });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyFine);
  };

  const handleSubmit = async () => {
    if (form.startDate === "" || form.endDate === "" || form.percentage === "") {
      toast.error(t("config.multa.error.requiredFields"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        startDate: Number(form.startDate),
        endDate: Number(form.endDate),
        percentage: Number(form.percentage),
      };
      if (editingId) {
        await updateFineConfig(editingId, payload);
        toast.success(t("config.multa.success.updated"));
      } else {
        await createFineConfig(payload);
        toast.success(t("config.multa.success.created"));
      }
      cancelEdit();
      fetchFines();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("config.multa.error.save"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (fine) => {
    setBusyId(fine.id);
    try {
      await deleteFineConfig(fine.id);
      toast.success(t("config.multa.success.deleted"));
      if (editingId === fine.id) cancelEdit();
      fetchFines();
    } catch (err) {
      toast.error(t("config.multa.error.delete"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <p className='text-neutral-500'>{t("config.multa.subtitle")}</p>

      <div className='row gy-3 align-items-end mb-24'>
        <div className='col-sm-3'>
          <label className='form-label'>{t("config.multa.field.startDate")}</label>
          <input type='number' min='0' className='form-control' value={form.startDate} onChange={setField("startDate")} />
        </div>
        <div className='col-sm-3'>
          <label className='form-label'>{t("config.multa.field.endDate")}</label>
          <input type='number' min='0' className='form-control' value={form.endDate} onChange={setField("endDate")} />
        </div>
        <div className='col-sm-3'>
          <label className='form-label'>{t("config.multa.field.percentage")}</label>
          <div className='icon-field'>
            <span className='icon'>
              <Icon icon='solar:percent-square-outline' />
            </span>
            <input type='number' min='0' className='form-control' value={form.percentage} onChange={setField("percentage")} />
          </div>
        </div>
        <div className='col-sm-3 d-flex gap-2'>
          <button type='button' className='btn btn-primary-600 w-100' onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              t("global.loading")
            ) : editingId ? (
              <>
                <i className='ri-save-line' /> {t("global.save")}
              </>
            ) : (
              <>
                <i className='ri-add-line' /> {t("config.multa.action.add")}
              </>
            )}
          </button>
          {editingId && (
            <button type='button' className='btn btn-outline-neutral-500' onClick={cancelEdit} disabled={submitting}>
              {t("global.cancel")}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className='text-center py-40'>{t("global.loading")}</div>
      ) : fines.length === 0 ? (
        <div className='alert alert-info mb-0'>{t("config.multa.hint.empty")}</div>
      ) : (
        <div className='table-responsive'>
          <table className='table bordered-table mb-0'>
            <thead>
              <tr>
                <th>{t("config.multa.field.startDate")}</th>
                <th>{t("config.multa.field.endDate")}</th>
                <th>{t("config.multa.field.percentage")}</th>
                <th>{t("turma.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {fines.map((fine) => (
                <tr key={fine.id}>
                  <td>{fine.startDate}</td>
                  <td>{fine.endDate}</td>
                  <td>{fine.percentage}%</td>
                  <td>
                    <div className='d-flex gap-2'>
                      <button type='button' className='btn btn-outline-primary-600 btn-sm d-flex align-items-center gap-1' onClick={() => startEdit(fine)}>
                        <Icon icon='mdi:pencil-outline' />
                        {t("global.edit")}
                      </button>
                      <button
                        type='button'
                        className='btn btn-outline-danger-600 btn-sm d-flex align-items-center gap-1'
                        onClick={() => handleDelete(fine)}
                        disabled={busyId === fine.id}
                      >
                        <Icon icon='mdi:trash-can-outline' />
                        {t("global.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pagamentos — um único registo por sucursal (PaymentConfig)
// ---------------------------------------------------------------------------
const emptyPayment = { paymentStartDay: "", paymentEndDay: "", initialMonth: "", smsSenderID: "" };

const PagamentosTab = ({ t }) => {
  const [configId, setConfigId] = useState(null);
  const [form, setForm] = useState(emptyPayment);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    getPaymentConfig()
      .then((resp) => {
        const existing = (resp.data || [])[0];
        if (existing) {
          setConfigId(existing.id);
          setForm({
            paymentStartDay: existing.paymentStartDay ?? "",
            paymentEndDay: existing.paymentEndDay ?? "",
            initialMonth: existing.initialMonth ?? "",
            smsSenderID: existing.smsSenderID ?? "",
          });
        }
      })
      .catch(() => toast.error(t("config.pagamento.error.load")))
      .finally(() => setLoading(false));
  }, [t]);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        paymentStartDay: form.paymentStartDay === "" ? undefined : Number(form.paymentStartDay),
        paymentEndDay: form.paymentEndDay === "" ? undefined : Number(form.paymentEndDay),
        initialMonth: form.initialMonth === "" ? undefined : Number(form.initialMonth),
        smsSenderID: form.smsSenderID || undefined,
      };
      if (configId) {
        await updatePaymentConfig(configId, payload);
      } else {
        const resp = await createPaymentConfig(payload);
        setConfigId(resp.data?.id || null);
      }
      toast.success(t("config.pagamento.success.saved"));
    } catch (err) {
      toast.error(err?.response?.data?.error || t("config.pagamento.error.save"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className='text-center py-40'>{t("global.loading")}</div>;

  return (
    <div>
      <p className='text-neutral-500'>{t("config.pagamento.subtitle")}</p>
      <div className='row gy-3'>
        <div className='col-sm-3'>
          <label className='form-label'>{t("config.pagamento.field.paymentStartDay")}</label>
          <input type='number' min='1' max='31' className='form-control' value={form.paymentStartDay} onChange={setField("paymentStartDay")} />
        </div>
        <div className='col-sm-3'>
          <label className='form-label'>{t("config.pagamento.field.paymentEndDay")}</label>
          <input type='number' min='1' max='31' className='form-control' value={form.paymentEndDay} onChange={setField("paymentEndDay")} />
        </div>
        <div className='col-sm-3'>
          <label className='form-label'>{t("config.pagamento.field.initialMonth")}</label>
          <input type='number' min='1' max='12' className='form-control' value={form.initialMonth} onChange={setField("initialMonth")} />
        </div>
        <div className='col-sm-3'>
          <label className='form-label'>{t("config.pagamento.field.smsSenderID")}</label>
          <input type='text' className='form-control' value={form.smsSenderID} onChange={setField("smsSenderID")} />
        </div>
      </div>
      <div className='d-flex justify-content-end mt-24'>
        <button type='button' className='btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            t("global.loading")
          ) : (
            <>
              <i className='ri-save-line' /> {t("global.save")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pautas — interruptores globais, por sucursal, sobre a visualização de
// Pautas no WhatsApp
// ---------------------------------------------------------------------------
const PautasTab = ({ t }) => {
  const [pautaVisivelWhatsapp, setPautaVisivelWhatsapp] = useState(true);
  const [pautaBloqueiaComPagamentoEmAtraso, setPautaBloqueiaComPagamentoEmAtraso] = useState(true);
  // Ao contrário das flags de pauta acima (default "ligado"), o modelo
  // Cambridge muda radicalmente a escala de notas (0-20 -> %) em todos os
  // ecrãs de Gestão Académica — tem de ser opt-in explícito, por isso o
  // default aqui é "desligado" a não ser que a sucursal tenha isCambridge==1.
  const [isCambridge, setIsCambridge] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    getSucursal()
      .then((resp) => {
        const sucursal = resp.data || {};
        setPautaVisivelWhatsapp(isFlagOn(sucursal.pautaVisivelWhatsapp));
        setPautaBloqueiaComPagamentoEmAtraso(isFlagOn(sucursal.pautaBloqueiaComPagamentoEmAtraso));
        // eslint-disable-next-line eqeqeq
        setIsCambridge(sucursal.isCambridge == 1);
      })
      .catch(() => toast.error(t("config.pauta.error.load")))
      .finally(() => setLoading(false));
  }, [t]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await updateSucursalPautaConfig(null, {
        pautaVisivelWhatsapp: pautaVisivelWhatsapp ? 1 : 0,
        pautaBloqueiaComPagamentoEmAtraso: pautaBloqueiaComPagamentoEmAtraso ? 1 : 0,
        isCambridge: isCambridge ? 1 : 0,
      });
      toast.success(t("config.pauta.success.saved"));
    } catch (err) {
      toast.error(err?.response?.data?.error || t("config.pauta.error.save"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className='text-center py-40'>{t("global.loading")}</div>;

  return (
    <div>
      <p className='text-neutral-500'>{t("config.pauta.subtitle")}</p>

      <div className='d-flex align-items-start gap-3 py-16 border-bottom'>
        <div className='form-switch mt-4'>
          <input
            className='form-check-input'
            type='checkbox'
            role='switch'
            checked={pautaVisivelWhatsapp}
            onChange={(e) => setPautaVisivelWhatsapp(e.target.checked)}
          />
        </div>
        <div>
          <h6 className='text-md mb-4'>{t("config.pauta.field.visivel")}</h6>
          <p className='text-neutral-500 text-sm mb-0'>{t("config.pauta.field.visivel.hint")}</p>
        </div>
      </div>

      <div className='d-flex align-items-start gap-3 py-16'>
        <div className='form-switch mt-4'>
          <input
            className='form-check-input'
            type='checkbox'
            role='switch'
            checked={pautaBloqueiaComPagamentoEmAtraso}
            onChange={(e) => setPautaBloqueiaComPagamentoEmAtraso(e.target.checked)}
            disabled={!pautaVisivelWhatsapp}
          />
        </div>
        <div>
          <h6 className='text-md mb-4'>{t("config.pauta.field.bloqueiaAtraso")}</h6>
          <p className='text-neutral-500 text-sm mb-0'>{t("config.pauta.field.bloqueiaAtraso.hint")}</p>
        </div>
      </div>

      <div className='d-flex align-items-start gap-3 py-16 border-top'>
        <div className='form-switch mt-4'>
          <input
            className='form-check-input'
            type='checkbox'
            role='switch'
            checked={isCambridge}
            onChange={(e) => setIsCambridge(e.target.checked)}
          />
        </div>
        <div>
          <h6 className='text-md mb-4'>{t("config.pauta.field.cambridge")}</h6>
          <p className='text-neutral-500 text-sm mb-0'>{t("config.pauta.field.cambridge.hint")}</p>
        </div>
      </div>

      <div className='d-flex justify-content-end mt-24'>
        <button type='button' className='btn btn-primary-600 px-32' onClick={handleSave} disabled={submitting}>
          {submitting ? (
            t("global.loading")
          ) : (
            <>
              <i className='ri-save-line' /> {t("global.save")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ConfiguracoesLayer;
